import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getDeceasedInfos } from '../api/deceased_req';
import CondolenceModal from '../components/CondolenceModal';
import '../style/ObituaryDetail.css';

export default function ObituaryDetail() {
    const { id, agencyId } = useParams();
    const navigate = useNavigate();
    const [deceased, setDeceased] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchDeceased = async () => {
            setLoading(true);
            const response = await getDeceasedInfos(id);

            if (response) {
                setDeceased(response);
                // Extract agencyId from deceased data
            } else {
                console.error('Error fetching deceased.');
            }
            setLoading(false);
        };

        fetchDeceased();
    }, [id]);

    const formatDate = (dateObj) => {
        if (!dateObj || !dateObj.date) return '';
        const date = new Date(dateObj.date);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculateAge = (birthDate, deathDate) => {
        if (!birthDate || !deathDate) return null;
        const birth = new Date(birthDate.date);
        const death = new Date(deathDate.date);
        let age = death.getFullYear() - birth.getFullYear();
        const monthDiff = death.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && death.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    // Helper to get marital status (API can provide either marital_status or marital_status_type)
    const getMaritalStatus = () => {
        return deceased.marital_status || deceased.marital_status_type;
    };


    // Helper to darken/lighten color
    const adjustColor = (color, amount) => {
        if (!color) return color;
        return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
    };

    if (loading) {
        return (
            <div className="detail-loading">
                <p>Chargement de l'avis de décès...</p>
            </div>
        );
    }

    if (!deceased) {
        return (
            <div className="detail-error">
                <h2>Avis de décès non trouvé</h2>
                <Link to={`/ listes - avis - deces / ${agencyId || '478'}/1`}>
                    <button className="back-button">
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Retour à la liste
                    </button>
                </Link >
            </div >
        );
    }

    const mainColor = deceased.main_color || '#5d4a92';
    const darkerMainColor = mainColor ? adjustColor(mainColor, -60) : '#000000';

    return (
        <div className="detail-container" style={{ backgroundColor: `${mainColor}10` }}>
            <div className="detail-wrapper">
                <div className="detail-card" style={{ borderTop: `6px solid ${mainColor}` }}>
                    <div className="detail-content">
                        <div className="detail-main-section">
                            <div className="detail-info-left">
                                <h1 className="detail-name" style={{ fontSize: '1.5rem', marginTop: '-10px', marginBottom: '20px' }}>
                                    <span style={{ color: '#777' }}>{deceased.civility_long}</span> <span style={{ color: mainColor }}>{deceased.first_name} {deceased.last_name}</span>
                                </h1>
                                <div className="detail-condolences">
                                    {deceased.header_announcement ? (
                                        <div dangerouslySetInnerHTML={{ __html: deceased.header_announcement }} />
                                    ) : (
                                        <>
                                            <p className="condolences-intro">
                                                Les proches et la famille ont la tristesse de vous faire part du décès de
                                            </p>
                                            <p className="condolences-name" style={{ color: mainColor }}>
                                                {deceased.full_name_long || `${deceased.first_name} ${deceased.last_name}`}
                                                {deceased.maiden_name && ` née ${deceased.maiden_name}`}
                                            </p>
                                            <p>survenu le {deceased.death_date ? formatDate(deceased.death_date) : 'date inconnue'}{deceased.death_place_city && ` à ${deceased.death_place_city}`}.</p>
                                        </>
                                    )}

                                    {deceased.header_birth ? (
                                        <div style={{ marginTop: '15px' }} dangerouslySetInnerHTML={{ __html: deceased.header_birth }} />
                                    ) : (
                                        deceased.birth_place_city && (
                                            <p className="condolences-location" style={{ marginTop: '15px' }}>
                                                {(deceased.civility_short === 'MR' || deceased.civility_short === 'Monsieur' || deceased.civility_short === 'M.') ? 'Il est né' : 'Elle est née'} à {deceased.birth_place_city},
                                                {deceased.birth_date && ` il y a ${calculateAge(deceased.birth_date, deceased.death_date)} ans`} et résidait à {deceased.city}.
                                                {deceased.thoughts_to ? (
                                                    deceased.thoughts_to
                                                ) : (
                                                    <>
                                                        {(getMaritalStatus() === 1 || getMaritalStatus() === 5 || getMaritalStatus() === 6 || getMaritalStatus() === 7) && deceased.conjoint && (
                                                            <>
                                                                Tout notre soutien à {' '}
                                                                {(getMaritalStatus() !== 1 && getMaritalStatus() !== 7) ? (
                                                                    // Compagnon/compagne pour statut 5, 6
                                                                    deceased.conjoint.civility === 'MR' ? 'son compagnon' :
                                                                        deceased.conjoint.civility === 'MME' ? 'sa compagne' :
                                                                            deceased.conjoint.civility
                                                                ) : (
                                                                    // Mari/femme pour statut 1 and 7
                                                                    deceased.conjoint.civility === 'MR' ? 'son mari' :
                                                                        deceased.conjoint.civility === 'MME' ? 'sa femme' :
                                                                            deceased.conjoint.civility
                                                                )}
                                                                {' '}{deceased.conjoint.firstname}
                                                                {deceased.children && deceased.children.length > 0 ? '' : '.'}
                                                            </>
                                                        )}
                                                        {deceased.children && deceased.children.length > 0 && (
                                                            <>
                                                                {deceased.children.length > 1 ? (
                                                                    <>
                                                                        {(getMaritalStatus() === 1 || getMaritalStatus() === 5 || getMaritalStatus() === 6 || getMaritalStatus() === 7) && deceased.conjoint && ' et '}
                                                                        {!deceased.conjoint && 'Tout notre soutien à '}
                                                                        ses enfants {deceased.children.map((child, index) => (
                                                                            <React.Fragment key={index}>
                                                                                {index > 0 && (index === deceased.children.length - 1 ? ' et ' : ', ')}
                                                                                {child.firstname}
                                                                            </React.Fragment>
                                                                        ))}.
                                                                    </>
                                                                ) : (
                                                                    deceased.children.map((child, index) => (
                                                                        <React.Fragment key={index}>
                                                                            {(getMaritalStatus() === 1 || getMaritalStatus() === 5 || getMaritalStatus() === 6 || getMaritalStatus() === 7) && deceased.conjoint && ' et '}
                                                                            {!deceased.conjoint && 'Tout notre soutien à '}
                                                                            {child.civility === 'MR' || child.civility === 'M.' ? 'son fils' :
                                                                                (child.civility === 'MME' || child.civility === 'MLLE') ? 'sa fille' : ''}
                                                                            {' '}{child.firstname}.
                                                                        </React.Fragment>
                                                                    ))
                                                                )}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </p>
                                        )
                                    )}
                                </div>
                                <div className="condolences-actions">
                                    <h3>ESPACE CONDOLÉANCES</h3>
                                    <p className="condolences-subtitle">TÉMOIGNER, ÉCHANGER, PARTAGER...</p>
                                    <div className="condolences-buttons">
                                        <button
                                            className="condolences-btn deposit-btn"
                                            style={{ backgroundColor: mainColor, borderColor: mainColor }}
                                            onClick={() => setShowModal(true)}
                                        >
                                            DÉPOSER SES CONDOLÉANCES
                                        </button>
                                        <button
                                            className="condolences-btn view-btn"
                                            style={{ color: mainColor, borderColor: mainColor }}
                                            onClick={() => navigate(`/condolences/${agencyId}/1/${deceased.id}`)}
                                        >
                                            VOIR LES CONDOLÉANCES
                                        </button>
                                    </div>
                                </div>
                                <div className="detail-ceremonies" style={{ '--strong-color': darkerMainColor }}>
                                    {/* Recueillements */}
                                    {deceased.recueillements > 0 && deceased.recueillement_address && (
                                        <div className="ceremony-block">
                                            <div className="ceremony-header">
                                                <h2 style={{ color: mainColor }}>Le recueillement</h2>
                                                <div className="ceremony-main-date">
                                                    {deceased.recueillement_date || ''}
                                                </div>
                                            </div>
                                            <div className="ceremony-section">
                                                <div className="ceremony-content">
                                                    {deceased.recueillement_address && (
                                                        <div dangerouslySetInnerHTML={{ __html: deceased.recueillement_address }} />
                                                    )}

                                                    {deceased.recueillement_comment && (
                                                        <div style={{ fontStyle: 'italic', marginTop: '10px' }} dangerouslySetInnerHTML={{ __html: deceased.recueillement_comment }} />
                                                    )}

                                                    {deceased.recueillement_adds && deceased.recueillement_adds.length > 0 && (
                                                        <>
                                                            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                                                            {deceased.recueillement_adds.map((add, index) => (
                                                                <div key={`recueillement-add-${index}`} style={{ marginTop: '15px', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: add }} />
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Rassemblements */}
                                    {deceased.rassemblements > 0 && deceased.rassemblement_address && (
                                        <div className="ceremony-block">
                                            <div className="ceremony-header">
                                                <h2 style={{ color: mainColor }}>Le rassemblement</h2>
                                                <div className="ceremony-main-date">
                                                    {deceased.rassemblement_date || ''}
                                                </div>
                                            </div>
                                            <div className="ceremony-section">
                                                <div className="ceremony-content">
                                                    {deceased.rassemblement_address && (
                                                        <div dangerouslySetInnerHTML={{ __html: deceased.rassemblement_address }} />
                                                    )}

                                                    {deceased.rassemblement_comment && (
                                                        <div style={{ marginTop: '15px', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: deceased.rassemblement_comment }} />
                                                    )}

                                                    {deceased.rassemblement_adds && deceased.rassemblement_adds.length > 0 && (
                                                        <>
                                                            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                                                            {deceased.rassemblement_adds.map((add, index) => (
                                                                <div key={`rassemblement-add-${index}`} className="sub-item" style={{ marginBottom: '10px' }} dangerouslySetInnerHTML={{ __html: add }} />
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Cérémonies */}
                                    {deceased.ceremonies > 0 && deceased.ceremony_address && (
                                        <div className="ceremony-block">
                                            <div className="ceremony-header">
                                                <h2 style={{ color: mainColor }}>La cérémonie</h2>
                                                <div className="ceremony-main-date">
                                                    {deceased.ceremony_date || ''}
                                                </div>
                                            </div>
                                            <div className="ceremony-section">
                                                <div className="ceremony-content">
                                                    {deceased.ceremony_address && (
                                                        <div dangerouslySetInnerHTML={{ __html: deceased.ceremony_address }} />
                                                    )}

                                                    {deceased.ceremony_comment && (
                                                        <div style={{ marginTop: '15px', lineHeight: '1.6', fontStyle: 'italic' }} dangerouslySetInnerHTML={{ __html: deceased.ceremony_comment }} />
                                                    )}

                                                    {deceased.ceremony_adds && deceased.ceremony_adds.length > 0 && (
                                                        <>
                                                            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                                                            {deceased.ceremony_adds.map((add, index) => (
                                                                <div key={`ceremony-add-${index}`} className="sub-item" style={{ marginBottom: '10px' }} dangerouslySetInnerHTML={{ __html: add }} />
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                            <div style={{ width: '350px', flexShrink: 0 }}>
                                <div className="detail-photo-right">
                                    <img src={deceased.portrait || '/assets/default.jpg'} alt={deceased.full_name_long || 'Photo'} />
                                </div>
                                {(deceased.birth_date || deceased.death_date || ((getMaritalStatus() === 1 || getMaritalStatus() === 5 || getMaritalStatus() === 6 || getMaritalStatus() === 7) && deceased.conjoint && (deceased.conjoint.firstname || deceased.conjoint.lastname))) && (
                                    <>
                                        <div style={{
                                            backgroundColor: '#e9ecef',
                                            padding: '12px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                            color: '#495057',
                                            borderTopLeftRadius: '8px',
                                            borderTopRightRadius: '8px',
                                            border: '2px solid #d0d0d0',
                                            borderBottom: 'none',
                                            marginTop: deceased.portrait ? '20px' : '0'
                                        }}>
                                            {deceased.first_name} {deceased.last_name}
                                        </div>
                                        <div className="detail-info-box" style={{ marginTop: '0', borderTopLeftRadius: '0', borderTopRightRadius: '0' }}>
                                            {deceased.birth_date && (
                                                <div className="detail-info-row">
                                                    <span className="detail-info-label">Né(e) le</span>
                                                    <span className="detail-info-value">
                                                        {formatDate(deceased.birth_date)}
                                                        {deceased.birth_place_city && ` à ${deceased.birth_place_city}`}
                                                    </span>
                                                </div>
                                            )}
                                            {deceased.death_date && (
                                                <div className="detail-info-row">
                                                    <span className="detail-info-label">Décédé(e) le</span>
                                                    <span className="detail-info-value">
                                                        {formatDate(deceased.death_date)}
                                                        {deceased.city && ` à ${deceased.city}`}
                                                    </span>
                                                </div>
                                            )}
                                            {(getMaritalStatus() === 1 || getMaritalStatus() === 5 || getMaritalStatus() === 6 || getMaritalStatus() === 7) && deceased.conjoint && (deceased.conjoint.firstname || deceased.conjoint.lastname) && (
                                                <div className="detail-info-row">
                                                    <span className="detail-info-label">
                                                        {(getMaritalStatus() !== 1 && getMaritalStatus() !== 7) ? (
                                                            deceased.conjoint.civility === 'MR' ? 'Son compagnon' :
                                                                deceased.conjoint.civility === 'MME' ? 'Sa compagne' :
                                                                    'Son/Sa conjoint(e)'
                                                        ) : (
                                                            deceased.conjoint.civility === 'MR' ? 'Son mari' :
                                                                deceased.conjoint.civility === 'MME' ? 'Sa femme' :
                                                                    'Son époux/épouse'
                                                        )}
                                                    </span>
                                                    <span className="detail-info-value">
                                                        {deceased.conjoint.firstname} {deceased.conjoint.lastname}
                                                    </span>
                                                </div>
                                            )}
                                            {deceased.children && deceased.children.length > 0 && (
                                                <>
                                                    {deceased.children.length > 1 ? (
                                                        <div className="detail-info-row">
                                                            <span className="detail-info-label">Ses enfants</span>
                                                            <span className="detail-info-value">
                                                                {deceased.children.map((child, index) => (
                                                                    <React.Fragment key={index}>
                                                                        {index > 0 && (index === deceased.children.length - 1 ? ' et ' : ', ')}
                                                                        {child.firstname}
                                                                    </React.Fragment>
                                                                ))}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        deceased.children.map((child, index) => (
                                                            <div key={index} className="detail-info-row">
                                                                <span className="detail-info-label">
                                                                    {child.civility === 'MR' || child.civility === 'M.' ? 'Son fils' :
                                                                        (child.civility === 'MME' || child.civility === 'MLLE') ? 'Sa fille' : 'Son enfant'}
                                                                </span>
                                                                <span className="detail-info-value">
                                                                    {child.firstname} {child.lastname}
                                                                </span>
                                                            </div>
                                                        ))
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                                {/* Bouton Offrir des fleurs de deuil */}
                                {deceased.agency && deceased.agency.send_flowers === 1 && deceased.agency.url_shop && (
                                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                        <a
                                            href={deceased.agency.url_shop}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'block',
                                                textAlign: 'center',
                                                padding: '12px 0',
                                                backgroundColor: '#fff',
                                                color: mainColor,
                                                border: `2px solid ${mainColor}`,
                                                borderRadius: '4px',
                                                textDecoration: 'none',
                                                fontWeight: '600',
                                                fontSize: '0.95rem',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = mainColor;
                                                e.target.style.color = '#fff';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#fff';
                                                e.target.style.color = mainColor;
                                            }}
                                        >
                                            Offrir des fleurs de deuil
                                        </a>
                                    </div>
                                )}
                                {/* Encart Agence */}
                                {deceased.agency && (
                                    <div style={{ marginTop: '40px', marginBottom: '20px' }}>
                                        <div className="detail-info-box" style={{ textAlign: 'center' }}>
                                            <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                                                Entreprise de Pompes Funèbres
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '15px' }}>
                                                {deceased.agency.name}
                                            </div>
                                            {deceased.agency.address && (
                                                <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                                                    {deceased.agency.address}
                                                </div>
                                            )}
                                            {deceased.agency.zipcode && deceased.agency.city && (
                                                <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                                                    {deceased.agency.zipcode} {deceased.agency.city}
                                                </div>
                                            )}
                                            {deceased.agency.phone && (
                                                <div style={{ fontSize: '14px', marginTop: '10px' }}>
                                                    Tél. : {deceased.agency.phone}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {deceased.message && (
                            <p className="detail-message">
                                {deceased.message}
                            </p>
                        )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        <Link to={`/listes-avis-deces/${agencyId}/1`}>
                            <button
                                className="back-button"
                                style={{ backgroundColor: mainColor }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                Retour à la liste
                            </button>
                        </Link>
                    </div>
                </div>
            </div>

            {showModal &&
                <CondolenceModal
                    deceased={deceased}
                    onClose={() => setShowModal(false)}
                    onSuccess={() => {
                        console.log('Condolence submitted successfully');
                    }}
                />
            }

        </div>
    );
}
